import { inject, Injectable, signal } from '@angular/core';
import * as AlertsJson from '../../../assets/json/alerts.json';
import { PickListGroup } from '../../components/core/controls/pick-list.component';
import { deepCopy } from '../../functions/deep-copy';
import {
  Alert,
  Condition,
  EmployeeValue,
  NotificationFrequency,
} from '../../models/api/alerts/alert';
import { AlertMessage } from '../../models/api/alerts/alert-message';
import { AlertConditionCard } from '../../models/api/alerts/alert-condition-card';
import {
  AlertConditionExpressionsResponse,
  AlertMiscField,
  AlertMiscFieldValue,
  AlertPayCode,
} from '../../models/api/alerts/alert-condition-code';
import { ListItem } from '../../models/api/core/list/list-item';
import { Api } from '../api/api.service';
import { BaseService } from '../base.service';
import { Session } from '../session/session.service';
import { StorageService } from '../storage/storage.service';
import { AlertsDataService } from './alerts-data.service';

@Injectable({ providedIn: 'root' })
export class AlertsService extends BaseService {
  private api = inject(Api);
  private storage = inject(StorageService);
  private session = inject(Session);
  private alertsDataService = inject(AlertsDataService);

  currentAlert = signal<Alert>(null);
  isEditMode = signal<boolean>(false);

  // Signal to trigger alerts list refresh
  alertsListVersion = signal<number>(0);

  conditionCards = signal<AlertConditionCard[]>([]);
  conditionExpressions = signal<AlertConditionExpressionsResponse | null>(null);

  lagFrom: ListItem[] = [];

  lagTo: ListItem[] = [];

  lagOp: ListItem[] = [];

  listItems: ListItem[] = [];

  workStatus: ListItem[] = [];

  benefitStatus: ListItem[] = [];

  claimStatusOptions: ListItem[] = [];

  loadedConditionCodes: ListItem[] = [];

  paymentOps: ListItem[] = [];

  amountOps: ListItem[] = [];

  boolItems: any[] = [];

  percentItems: any[] = [];

  financialItems: ListItem[] = [];

  percentRangeItems: any[] = [];

  locationMethod: ListItem[] = [];

  notifyItems: any[] = [];

  status: ListItem[] = [];

  alert: ListItem[] = [];

  payCodeTypes: PickListGroup[] = [];

  payCodeSeries100: any[] = [
    { label: '100 - Prenote (WC)', value: 100 },
    { label: '104 - Temporary Partial-Modified Duty (WC)', value: 104 },
    { label: '106 - Perm. Partial-Schedule (WC)', value: 106 },
    { label: '107 - Permanent Total Disability (WC)', value: 107 },
    { label: "131 - Add'l living expense (PR)", value: 131 },
    { label: '170 - Punitive Damages-Taxable (PR)', value: 170 },
    { label: '134 - Advance payment (GL)', value: 134 },
    { label: '171 - Non Physical Injuries-Taxable (GL)', value: 171 },
    { label: '197 - Miscellaneous Indemnity/Loss (GL)', value: 197 },
  ];
  payCodeSeries200: any[] = [
    { label: '299 - Converted Loss (AU)', value: 299 },
    { label: '216 - Mitigation/Remediation (AU)', value: 216 },
    { label: '220 - Gift Card (GL)', value: 220 },
    { label: '250 - Settlement of all claims (GL)', value: 250 },
    { label: '297 - Miscellaneous Loss (GL)', value: 297 },
    { label: '203 - Computers (PR)', value: 203 },
    { label: '204 - Valuable papers (PR)', value: 204 },
    { label: '205 - Jewelry (PR)', value: 205 },
  ];
  payCodeSeries300: any[] = [
    { label: '346 - Paramedic (GL)', value: 346 },
    { label: '370 - Punitive Damages-Taxable (GL)', value: 370 },
    { label: '371 - Non Physical Injuries-Taxable (GL)', value: 371 },
    { label: '300 - Ambulance (AU)', value: 300 },
    { label: '302 - Hospital (AU)', value: 302 },
    { label: '303 - Pharmacy (AU)', value: 303 },
    { label: '347 - PIP (lost  wages) (GL)', value: 347 },
    { label: '348 - Walk-in Clinic Fees (GL)', value: 348 },
  ];
  payCodeSeries400: any[] = [
    { label: '471 - Non Physical Injuries-Taxable (GL)', value: 471 },
    { label: '497 - Miscellaneous CL/Other (GL)', value: 497 },
    {
      label: '402 - Comprehensive Personal Liability Bodily Injury (PR)',
      value: 402,
    },
    { label: '403 - Personal Liability Medical Payments (PR)', value: 403 },
    { label: '405 - Debris removal (PR)', value: 405 },
    { label: '406 - Time element(bus. interruption) (PR)', value: 406 },
    { label: '400 - Total loss (AU)', value: 400 },
    { label: '401 - Partial loss (AU)', value: 401 },
    { label: '447 - Hospital Bill Audit Fee (GL)', value: 447 },
    { label: '448 - Utilization Review Fee (GL)', value: 448 },
    { label: '449 - PPO Network Fee (GL)', value: 449 },
  ];
  payCodeSeries500: any[] = [
    { label: '501 - Partial loss (AU)', value: 501 },
    { label: '502 - Lien holder (AU)', value: 502 },
    { label: '503 - Rental reimbursement (AU)', value: 503 },
    { label: '504 - Towing (AU)', value: 504 },
    { label: '554 - Structured settlement (GL)', value: 554 },
    { label: '570 - Punitive Damages-Taxable (GL)', value: 570 },
  ];
  payCodeSeries600: any[] = [
    { label: '687 - Medical Exam-Independent/Agredd (GL)', value: 687 },
    { label: '688 - Travel Expense (GL)', value: 688 },
    { label: '689 - Subcontractor Fee-Non Billable (GL)', value: 689 },
    { label: '601 - Appraisal/Appraisers (AU)', value: 601 },
    { label: '604 - Expert Testimony (AU)', value: 604 },
    { label: '605 - Building Inspection/Consultant (AU)', value: 605 },
    { label: '607 - Towing (PR)', value: 607 },
    { label: '608 - Allocated TPA Fees (WC)', value: 608 },
    { label: '612 - Economist/Accountant Consultant (WC)', value: 612 },
    { label: '620 - Gift Certificate (WC)', value: 620 },
  ];
  payCodeSeries700: any[] = [
    { label: '700 - Excess - Loss (AU)', value: 700 },
    { label: '701 - Salvage - Loss (AU)', value: 701 },
    { label: '703 - Contribution - Loss (GL)', value: 703 },
    { label: '706 - Subrogation - Loss (GL)', value: 706 },
    { label: '707 - Deductible - Loss (GL)', value: 707 },
    { label: '789 - Other Expense Recovery (WC)', value: 789 },
    { label: '790 - Converted Expense Recovery (WC)', value: 790 },
    { label: '791 - Penalty - TPA Reimbursement (WC)', value: 791 },
    { label: '786 - Damage Mitigation - Expense (PR)', value: 786 },
  ];

  claimTypes: ListItem[] = [];

  constructor() {
    super();
    this.dispose.subs = this.session.cacheCleared.subscribe(() => {
      // revert back to initial state
      this.currentAlert.set(null);
      this.isEditMode.set(false);
      this.alertsListVersion.set(0);
      this.conditionCards.set([]);
      this.conditionExpressions.set(null);
      this.storage.remove('alerts:current-alert');
    });

    this.loadCurrentAlert();

    this.addClaimTypes();

    this.add(this.lagFrom, 'Date of Loss', 'DATE_EVENT');
    this.add(this.lagFrom, 'Date Reported to Client', 'CLAIM_MADE_DATE');
    this.add(this.lagFrom, 'Date Reported by Client', 'DATE_RPT_BY_CLIENT');
    this.add(this.lagFrom, 'Date Reported to Sedgwick', 'DATE_RPTED_CMS');
    this.add(this.lagFrom, 'Date Claim Created', 'DATE_CREATE');
    this.add(this.lagFrom, 'Date Claim Opened', 'DATE_OPEN');
    this.add(this.lagFrom, 'Date Claim Reopened', 'DATE_STAT');
    this.add(this.lagFrom, 'Date Claim Closed', 'DATE_CLOSE');

    this.add(this.lagTo, 'Date Reported to Client', 'CLAIM_MADE_DATE');
    this.add(this.lagTo, 'Date Reported by Client', 'DATE_RPT_BY_CLIENT');
    this.add(this.lagTo, 'Date Reported to Sedgwick', 'DATE_RPTED_CMS');
    this.add(this.lagTo, 'Date Claim Created', 'DATE_CREATE');
    this.add(this.lagTo, 'Date Claim Opened', 'DATE_OPEN');
    this.add(this.lagTo, 'Date Claim Reopened', 'DATE_STAT');
    this.add(this.lagTo, 'Date Claim Closed', 'DATE_CLOSE');

    this.add(this.lagOp, 'Greater than or equal to', 'GreaterThanEqualTo');
    this.add(this.lagOp, 'Greater than', 'GreaterThan');
    this.add(this.lagOp, 'Less than or equal to', 'LessThanEqualTo');
    this.add(this.lagOp, 'Less than', 'LessThan');
    this.add(this.lagOp, 'Equal to', 'EqualTo');

    this.add(this.listItems, 'In list', 'InList');
    this.add(this.listItems, 'Not in list', 'NotInList');

    // Claim Status values - must match legacy ViaOneView alerts (jquery-alerts.js)
    this.add(this.claimStatusOptions, 'Accepted/Approved', 'A');
    this.add(this.claimStatusOptions, 'Closed', 'C');
    this.add(this.claimStatusOptions, 'Compromised', 'CP');
    this.add(this.claimStatusOptions, 'Deferred', 'F');
    this.add(this.claimStatusOptions, 'Deleted', 'L');
    this.add(this.claimStatusOptions, 'Denied', 'D');
    this.add(this.claimStatusOptions, 'Incident', 'I');
    this.add(this.claimStatusOptions, 'Open', 'O');
    this.add(this.claimStatusOptions, 'Pending', 'P');
    this.add(this.claimStatusOptions, 'Terminated', 'T');
    this.add(this.claimStatusOptions, 'JURIS Admin (DS Only)', 'J');
    this.add(this.claimStatusOptions, 'Reinstated (DS Only)', 'R');
    this.add(this.claimStatusOptions, 'Suspended (DS Only)', 'S');

    this.add(this.workStatus, 'Normal', 'N');
    this.add(this.workStatus, 'Off work', 'OW');
    this.add(this.workStatus, 'Restricted', 'R');
    this.add(this.workStatus, 'Restricted-NTL', 'RNTL');
    this.add(this.workStatus, 'Terminated', 'T');

    // Benefit Status values - must match legacy ViaOneView alerts (jquery-alerts.js)
    this.add(this.benefitStatus, 'Approved', 'OA');
    this.add(this.benefitStatus, 'Denied', 'OD');
    this.add(this.benefitStatus, 'Pending', 'OP');
    this.add(this.benefitStatus, 'Provisional Pay Approval', 'PA');
    this.add(this.benefitStatus, 'Waiting Period - Not Paid', 'WP');
    this.add(
      this.benefitStatus,
      'Waiting Period - Retro (Pay) (WC Only)',
      'WR',
    );

    this.add(this.paymentOps, 'Greater than', 'GreaterThan');
    this.add(this.paymentOps, 'Greater than or equal to', 'GreaterThanEqualTo');
    this.add(this.paymentOps, 'Less than', 'LessThan');
    this.add(this.paymentOps, 'Less than or equal to', 'LessThanEqualTo');
    this.add(this.paymentOps, 'Equal to', 'EqualTo');

    this.amountOps = deepCopy(this.paymentOps);
    this.add(this.amountOps, 'Increased by', 'IncreasedBy');
    this.add(this.amountOps, 'Decreased by', 'DecreasedBy');
    const decreasedBy = this.amountOps.pop();
    const increasedBy = this.amountOps.pop();
    this.amountOps.unshift(increasedBy, decreasedBy);

    this.add(this.boolItems, 'True', true);
    this.add(this.boolItems, 'False', false);

    this.add(this.percentItems, '25%', 25);
    this.add(this.percentItems, '50%', 50);
    this.add(this.percentItems, '75%', 75);
    this.add(this.percentItems, '90%', 90);
    this.add(this.percentItems, '100%', 100);

    this.add(this.financialItems, 'Incurred', 'incurred');
    this.add(this.financialItems, 'Paid', 'paid');

    this.add(this.percentRangeItems, 'Mid-Range (50%)', 1);
    this.add(this.percentRangeItems, 'At-Risk (70%)', 2);
    this.add(this.percentRangeItems, 'At-Risk - 7 (70% - 7)', 3);

    this.add(this.locationMethod, 'Account and unit', 'CAU');
    this.add(this.locationMethod, 'Corporate structure', 'Structure');

    this.add(this.notifyItems, '1 day', 1);
    this.add(this.notifyItems, '5 days', 5);
    this.add(this.notifyItems, '10 days', 10);

    // Status filter values: Database stored procedure expects uppercase NEW/READ
    // Note: API returns lowercase "read"/"new" but filter uses uppercase
    this.add(this.status, 'New', 'NEW');
    this.add(this.status, 'Read', 'READ');

    this.add(this.alert, 'Lagtime DOL to Opened GT 2', '1');
  }

  addPayCodes() {
    this.addPickListItem(
      this.payCodeTypes,
      '100 Series (Indemnity/Loss)',
      this.payCodeSeries100,
    );
    this.addPickListItem(
      this.payCodeTypes,
      '200 Series (Loss)',
      this.payCodeSeries200,
    );
    this.addPickListItem(
      this.payCodeTypes,
      '300 Series (Medical)',
      this.payCodeSeries300,
    );
    this.addPickListItem(
      this.payCodeTypes,
      '400 Series (Loss)',
      this.payCodeSeries400,
    );
    this.addPickListItem(
      this.payCodeTypes,
      '500 Series (Loss)',
      this.payCodeSeries500,
    );
    this.addPickListItem(
      this.payCodeTypes,
      '600 Series (Expense)',
      this.payCodeSeries600,
    );
    this.addPickListItem(
      this.payCodeTypes,
      '700 Series (Recovery)',
      this.payCodeSeries700,
    );
  }

  addClaimTypes() {
    // ClaimType values must be uppercase to match legacy ViaOneView
    this.add(this.claimTypes, 'Indemnity', 'IN');
    this.add(this.claimTypes, 'Medical Only', 'MO');
    this.add(this.claimTypes, 'Incident Only', 'IO');
    this.add(this.claimTypes, "Employer's Liability", 'EL');
  }

  find<T>(items: T[], value: any, name = 'value'): T {
    return items.find((x) => (x as any)[name] === value);
  }

  /**
   * Finds a matching item in the list or creates a fallback object.
   * Used for mapping stored values back to ListItem objects for dropdowns.
   * Also handles legacy data by checking description field if value doesn't match.
   */
  findOrCreate<T>(items: T[], value: any, name = 'value'): T {
    // First try to find by the specified field (usually 'value')
    const found = items.find((x) => (x as any)[name] === value);
    if (found) {
      return found;
    }

    // For legacy data, try to match by description (e.g., "Accepted/Approved" -> find item with that description)
    const foundByDescription = items.find(
      (x) => (x as any).description === value,
    );
    if (foundByDescription) {
      return foundByDescription;
    }

    // Return a fallback object if not found
    return { value, description: value, items: [], disabled: false } as T;
  }

  /**
   * Stores current alert in session storage.
   */
  storeCurrentAlert() {
    this.storage.set('alerts:current-alert', this.currentAlert());
  }

  loadCurrentAlert() {
    const alert = this.storage.get('alerts:current-alert') as Alert;
    this.currentAlert.set(alert);
  }

  resetConditionOptions(alert: Alert): void {
    alert.description = null;
    alert.condition = null;
    const a = alert as any;
    a.selectOp = null;
    a.selectItems = null;
    a.selectPickListItems = null;
    a.availablePickListItems = null;
    a.selectBoolean = null;
    a.percent = null;
    a.financial = null;
    a.days = null;
    a.notifyDays = null;
    a.amount = null;
    a.amountOp = null;
    a.customField = null;
    a.individuals = [];
    a.lagFrom = null;
    a.lagTo = null;
    a.lagOp = null;
    a.lagDays = null;
    a.openFrom = null;
    a.openTo = null;
  }

  resetAllOptions(alert: Alert): void {
    this.resetConditionOptions(alert);

    alert.linesOfBusiness = [];
    alert.linesOfBusinessCard = [];
    alert.statesOfJurisdiction = [];
    alert.statesOfLoss = [];
    alert.statesOfPayroll = [];
    alert.statesOfPremium = [];
    alert.locations = [];
    alert.locationIncludeType = 'Include';
    alert.locationLookupType = 'Unknown';

    alert.startDate = null;
    alert.endDate = null;
    alert.enabled = null;
    alert.sendEmail = false;
    alert.sendText = false;
    alert.additionalEmails = [];
    alert.smsPhones = [];
    alert.notificationFreq = null;
    alert.aggregateEmail = null;
    alert.includeAttachment = null;
    alert.maxTextsPerDay = null;

    const a = alert as any;
    a.locationMethod = null;
    a.accounts = null;
    a.structures = null;
    a.jurisdictionState = null;
    a.lossState = null;
    a.payrollState = null;
    a.premiumState = null;
  }

  async saveCurrentAlert() {
    const alert = this.currentAlert();
    if (!alert) {
      throw new Error('No current alert to save');
    }

    const alertId = await this.alertsDataService.saveAlert(alert);

    // Update the current alert with the returned ID (for new alerts)
    if (alertId && alert.alertId !== alertId) {
      alert.alertId = alertId;
      this.currentAlert.set(alert);
    }

    // Clear session storage after successful save
    this.storage.remove('alerts:current-alert');
  }

  async loadConditionCards(lob?: string): Promise<AlertConditionCard[]> {
    let allCards: AlertConditionCard[] = (AlertsJson as any).alerts || [];

    // REQ 3.5.005.2: Sort by favorite (top 5 most popular first), then alphabetically by title
    // Cards read left-to-right in alphabetical order after favorites
    allCards = allCards.sort((a, b) => {
      if (a.favorite !== b.favorite) {
        return a.favorite ? -1 : 1;
      }

      return (a.title || '').localeCompare(b.title || '');
    });

    const normalizedLob = this.normalizeLob(lob);

    if (!normalizedLob) {
      this.conditionCards.set([]);
      return [];
    }

    this.conditionCards.set(allCards);
    return allCards;

    // TODO: Confirm if we need rule condtions in v5. Frontend is aware of all reqs already.
    // const ruleConditions = await this.loadRuleConditions(normalizedLob);
    // const mapped = this.mapRuleConditionsToCards(ruleConditions, allCards);
    // this.conditionCards.set(mapped);
    // return mapped;
  }

  async loadConditionExpressions(
    ruleConditionId: number,
  ): Promise<AlertConditionExpressionsResponse | null> {
    if (!ruleConditionId) {
      this.conditionExpressions.set(null);
      return null;
    }

    const response = await this.api.newAlert.getConditionExpressions({
      ruleConditionId,
    });
    this.conditionExpressions.set(response);
    return response;
  }

  private normalizeLob(lob?: string): string {
    if (!lob) {
      return '';
    }

    return lob.trim();
  }

  async loadAlertById(alertId: number): Promise<Alert | null> {
    try {
      const alerts = await this.alertsDataService.getAlerts();
      const alert = alerts.find((x) => x.alertId === alertId);
      if (alert && alert.alertId > 0) {
        // Deep copy so wizard mutations don't affect the cached alert list
        const alertCopy = deepCopy(alert) as Alert;
        const alertLobs =
          alertCopy.linesOfBusiness?.length > 0
            ? alertCopy.linesOfBusiness
            : (alertCopy.linesOfBusinessCard ?? []);

        const primaryLob = alertLobs[0];
        if (primaryLob) {
          await this.loadConditionCards(primaryLob);
        }

        this.mapApiResponseToWizardState(alertCopy);
        this.currentAlert.set(alertCopy);
        this.isEditMode.set(true);
        return alertCopy;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  private mapApiResponseToWizardState(alert: Alert): void {
    const alertAny = alert as any;

    // Map conditionType → conditionCard from the DB-aligned card list.
    if (alert.condition?.conditionType) {
      const cached = this.conditionCards();
      const ctName = alert.condition.conditionType;
      const matchingCard = cached.find((c) => c.type === ctName);
      if (matchingCard) {
        alert.conditionCard = matchingCard;
      }
    }

    // Map contracts[0].contNum → client (as string) and preserve description
    if (alert.contracts?.length > 0) {
      alertAny.client = alert.contracts[0].contNum?.toString();
      alertAny.clientDescription = alert.contracts[0].description ?? '';
    }

    // Map linesOfBusiness → linesOfBusinessCard
    if (alert.linesOfBusiness?.length > 0) {
      alert.linesOfBusinessCard = [...alert.linesOfBusiness];
    }

    // locationMethod now uses LocationLookupType values directly ('CAU'/'Structure')
    alertAny.locationMethod = alert.locationLookupType;

    if (alert.locations?.length > 0) {
      const includeType = alert.locationIncludeType || 'Include';

      if (alert.locationLookupType === 'CAU') {
        // Map locations → accounts
        alertAny.accounts = {
          includeType: includeType,
          accounts: alert.locations.map((loc) => ({
            id: `${loc.accountNumber} / ${loc.unitNumber?.toString().padStart(4, '0')}`,
            accountNumber: loc.accountNumber?.toString(),
            accountName: loc.locationName,
            unitNumber: loc.unitNumber?.toString().padStart(4, '0'),
            unitName: loc.locationName,
          })),
        };
        alertAny.structures = [];
      } else if (alert.locationLookupType === 'Structure') {
        // Map locations → structures
        alertAny.structures = {
          includeType: includeType,
          nodes: alert.locations.map((loc) => ({
            corpStructId: `node-${loc.corpStructNodeID}`,
            name: loc.locationName || '',
            accountNumber: loc.accountNumber ?? null,
            unitNumber: loc.unitNumber ?? null,
            code: loc.code || null,
            node: loc.corpStructNodeID,
          })),
        };
        alertAny.accounts = { includeType: 'Include', accounts: [] };
      }
    } else {
      alertAny.accounts = { includeType: 'Include', accounts: [] };
      alertAny.structures = [];
    }

    // Map condition values back to wizard inputs based on condition type
    this.mapConditionValuesToWizardState(alert);
  }

  /**
   * Maps condition values back to wizard inputs based on condition type.
   * Extracts values from condition.newValues array and operatorType enum.
   */
  private mapConditionValuesToWizardState(alert: Alert): void {
    const alertAny = alert as any;

    if (!alert.condition?.conditionType) {
      return;
    }

    // Extract value1/value2 from newValues array for backward-compatible wizard state
    const value1 = this.getConditionValue(alert.condition, 0);
    const value2 = this.getConditionValue(alert.condition, 1);

    // operatorType is already a string like 'InList', 'NotInList', 'EqualTo', etc.
    const operatorStr = alert.condition.operatorType;

    // Check if this is an InList/NotInList operator that uses array format
    const isInListOperator =
      alert.condition.operatorType === 'InList' ||
      alert.condition.operatorType === 'NotInList';

    switch (alert.condition.conditionType) {
      // Select options (ClaimStatus, ClaimType, WorkStatus, BenefitStatus)
      case 'ClaimStatus':
        if (isInListOperator && alert.condition.newValues?.length > 0) {
          alertAny.selectItems = alert.condition.newValues
            .filter((nv) => nv.value)
            .map((nv) => nv.value);
        } else if (value1) {
          alertAny.selectItems = value1
            .split(',')
            .map((v: string) => v.trim())
            .filter((v: string) => v.length > 0);
        }

        if (operatorStr) {
          alertAny.selectOp = this.find(this.listItems, operatorStr);
        }

        break;

      case 'ClaimType':
        if (isInListOperator && alert.condition.newValues?.length > 0) {
          alertAny.selectItems = alert.condition.newValues
            .filter((nv) => nv.value)
            .map((nv) => nv.value);
        } else if (value1) {
          alertAny.selectItems = value1
            .split(',')
            .map((v: string) => v.trim())
            .filter((v: string) => v.length > 0);
        }

        if (operatorStr) {
          alertAny.selectOp = this.find(this.listItems, operatorStr);
        }

        break;

      case 'WorkStatus': {
        // Map legacy work-status strings to short codes
        const workStatusLegacyToCode: Record<string, string> = {
          NORMAL: 'N',
          'OFF WORK': 'OW',
          RESTRICTED: 'R',
          'RESTRICTED-NTL': 'RNTL',
          TERMINATED: 'T',
        };
        const toCode = (v: string) =>
          workStatusLegacyToCode[v?.toUpperCase()] ?? v;

        if (isInListOperator && alert.condition.newValues?.length > 0) {
          alertAny.selectItems = alert.condition.newValues
            .filter((nv) => nv.value)
            .map((nv) => toCode(nv.value));
        } else if (value1) {
          alertAny.selectItems = value1
            .split(',')
            .map((v: string) => toCode(v.trim()))
            .filter((v: string) => v.length > 0);
        }

        if (operatorStr) {
          alertAny.selectOp = this.find(this.listItems, operatorStr);
        }

        break;
      }

      case 'BenefitStatus':
        if (isInListOperator && alert.condition.newValues?.length > 0) {
          alertAny.selectItems = alert.condition.newValues
            .filter((nv) => nv.value)
            .map((nv) => nv.value);
        } else if (value1) {
          alertAny.selectItems = value1
            .split(',')
            .map((v: string) => v.trim())
            .filter((v: string) => v.length > 0);
        }

        if (operatorStr) {
          alertAny.selectOp = this.find(this.listItems, operatorStr);
        }

        break;

      case 'CauseCode':
      case 'NatureResult': {
        const shouldUseInListFormat =
          isInListOperator ||
          (operatorStr === 'Unknown' && alert.condition.newValues?.length > 0);

        if (shouldUseInListFormat && alert.condition.newValues?.length > 0) {
          const valueCounts = new Map<string, number>();
          alertAny.selectItems = alert.condition.newValues
            .filter((nv) => nv.value)
            .map((nv) => {
              const count = valueCounts.get(nv.value) || 0;
              valueCounts.set(nv.value, count + 1);
              return count === 0 ? nv.value : `${nv.value}_${count}`;
            });
        } else if (value1) {
          alertAny.selectItems = value1
            .split(',')
            .map((v: string) => v.trim())
            .filter((v: string) => v.length > 0);
        }

        if (operatorStr === 'Unknown') {
          alertAny.selectOp = this.find(this.listItems, 'InList');
        } else if (operatorStr) {
          alertAny.selectOp = this.find(this.listItems, operatorStr);
        }

        break;
      }

      case 'BodyPart': {
        const shouldUseInListFormat =
          isInListOperator ||
          (operatorStr === 'Unknown' && alert.condition.newValues?.length > 0);

        if (shouldUseInListFormat && alert.condition.newValues?.length > 0) {
          const valueCounts = new Map<string, number>();
          alertAny.selectItems = alert.condition.newValues
            .filter((nv) => nv.value)
            .map((nv) => {
              const count = valueCounts.get(nv.value) || 0;
              valueCounts.set(nv.value, count + 1);
              return count === 0 ? nv.value : `${nv.value}_${count}`;
            });
        } else if (value1) {
          alertAny.selectItems = value1
            .split(',')
            .map((v: string) => v.trim())
            .filter((v: string) => v.length > 0);
        }

        if (operatorStr === 'Unknown') {
          alertAny.selectOp = this.find(this.listItems, 'InList');
        } else if (operatorStr) {
          alertAny.selectOp = this.find(this.listItems, operatorStr);
        }

        break;
      }

      case 'PaymentPayCode':
        // For InList operators, extract all values from newValues array
        if (isInListOperator && alert.condition.newValues?.length > 0) {
          alertAny.selectPickListItems = alert.condition.newValues
            .filter((nv) => nv.value)
            .map((nv) => ({
              value: nv.value,
              lineOfBusiness: nv.lob,
            }));
        } else if (value1) {
          // Legacy CSV format fallback
          alertAny.selectPickListItems = value1
            .split(',')
            .map((v: string) => ({ value: v.trim() }));
        }

        break;

      // Boolean options — backend always saves 'Y'/'N' via NormalizeFlagBooleanCondition
      case 'Litigation':
      case 'Subrogation':
        if (value1) {
          const upper = value1.trim().toUpperCase();
          if (upper === 'Y' || upper === 'N') {
            alertAny.selectBoolean = this.find(this.boolItems, upper === 'Y');
          }
        }

        break;

      // Amount options
      case 'TotalIncurred':
      case 'ReserveChange':
      case 'PaymentAmount':
        if (value1) {
          alertAny.amount = parseFloat(value1);
        }

        if (operatorStr) {
          alertAny.amountOp = this.find(this.amountOps, operatorStr);
        }

        break;

      // Days options
      case 'ClaimDateOpen':
      case 'ExcessNotificationDate': {
        const daysValue = value1 || value2;
        if (daysValue) {
          alertAny.days = parseInt(daysValue, 10);
        }

        break;
      }
      // Days with notify options
      case 'CalendarRestrictedDays':
      case 'AggregateCalendarLostDays':
      case 'AggregateCalendarRestrictedDays':
      case 'CalendarLostDays':
      case 'TCMOpenReferralDays': {
        const threshold = value1 || value2;
        if (threshold) {
          alertAny.days = parseInt(threshold, 10);
        }

        if (value1 && value2) {
          alertAny.notifyDays = parseInt(value2, 10);
        }

        break;
      }

      case 'LostTimeGuidelines': {
        const threshold = value1 || value2;
        if (threshold) {
          alertAny.days = this.find(
            this.percentRangeItems,
            parseInt(threshold, 10),
          );
        }

        if (value1 && value2) {
          alertAny.notifyDays = parseInt(value2, 10);
        }

        break;
      }

      // Lag time options
      case 'LagTimeAlerts': {
        const lagLegacyToColumn: Record<string, string> = {
          loss: 'DATE_EVENT',
          'reported-to': 'CLAIM_MADE_DATE',
          'reported-by': 'DATE_RPT_BY_CLIENT',
          'reported-sedgwick': 'DATE_RPTED_CMS',
          created: 'DATE_CREATE',
          opened: 'DATE_OPEN',
          reopened: 'DATE_STAT',
          closed: 'DATE_CLOSE',
        };
        const normalizeLagValue = (v: string): string =>
          lagLegacyToColumn[v] ?? v;

        if (value1 && value1.includes('|')) {
          const parts = value1.split('|');
          if (parts.length >= 4) {
            alertAny.lagFrom = this.find(
              this.lagFrom,
              normalizeLagValue(parts[0]),
            );
            alertAny.lagTo = this.find(this.lagTo, normalizeLagValue(parts[1]));
            alertAny.lagOp = this.find(this.lagOp, parts[2]);
            alertAny.lagDays = parseInt(parts[3], 10) || null;
          }
        } else if (alert.condition.newValues?.length >= 3) {
          const fromVal = alert.condition.newValues.find(
            (nv) => nv.name?.toLowerCase() === 'fromdate',
          );
          const toVal = alert.condition.newValues.find(
            (nv) => nv.name?.toLowerCase() === 'todate',
          );
          const daysVal = alert.condition.newValues.find(
            (nv) => nv.name?.toLowerCase() === 'lagdays',
          );

          if (fromVal?.value) {
            alertAny.lagFrom = this.find(
              this.lagFrom,
              normalizeLagValue(fromVal.value),
            );
          }

          if (toVal?.value) {
            alertAny.lagTo = this.find(
              this.lagTo,
              normalizeLagValue(toVal.value),
            );
          }

          if (alert.condition.operatorType) {
            alertAny.lagOp = this.find(
              this.lagOp,
              alert.condition.operatorType,
            );
          }

          if (daysVal?.value) {
            alertAny.lagDays = parseInt(daysVal.value, 10) || null;
          }
        }

        break;
      }

      case 'PolicyDeductibleSirRange':
      case 'PolicyLimit': {
        let percentValue: string | undefined;
        let financialValue: string | undefined;
        let notifyValue: string | undefined;

        if (value1 && value1.includes('|')) {
          const parts = value1.split('|');
          percentValue = parts[0];
          financialValue = parts[1];
          notifyValue = parts[2];
        } else {
          percentValue = value1 || undefined;
          const financialNv = this.getConditionValue(alert.condition, 2);
          financialValue = financialNv ? financialNv.toLowerCase() : undefined;
          notifyValue = value2 || undefined;
        }

        if (percentValue) {
          const percentNumber = parseFloat(percentValue);
          alertAny.percent = this.find(this.percentItems, percentNumber);
        }

        if (financialValue) {
          alertAny.financial = this.find(this.financialItems, financialValue);
        }

        if (
          notifyValue &&
          this.notifyItems.some(
            (item) => item.value === parseInt(notifyValue, 10),
          )
        ) {
          alertAny.notifyDays = parseInt(notifyValue, 10);
        }

        break;
      }

      case 'ClientDefinedField': {
        const nvArr = alert.condition?.newValues;
        if (nvArr && nvArr.length > 0) {
          if (nvArr[0]?.name === 'FieldNum') {
            alertAny.customField = this.loadClientDefinedFieldNamed(
              nvArr,
              alert,
            );
          } else {
            alertAny.customField = this.loadClientDefinedFieldPipe(nvArr[0]);
          }
        }
        break;
      }

      case 'IndividualHasClaimCase':
        alertAny.individuals = (alert.condition?.employeeCollection ?? []).map(
          (emp: any): EmployeeValue => ({
            empUnum: emp.empUnum ?? null,
            employeeID: emp.employeeID ?? null,
            firstName: emp.firstName ?? null,
            lastName: emp.lastName ?? null,
            ssn: emp.ssn ?? null,
            ssn4: emp.ssN4 ?? (emp.ssn ? emp.ssn.toString().slice(-4) : null),
            dateHire: emp.dateHire ?? null,
            recSource:
              emp.recSource === 'H' || emp.recSource === 'HR'
                ? 'HR'
                : emp.recSource === 'C' || emp.recSource === 'Claimant'
                  ? 'Claimant'
                  : emp.recSource ?? null,
          }),
        );
        break;

      default:
        // For other types, no specific mapping needed
        break;
    }
  }

  /** Parses the old single-entry pipe-delimited format for ClientDefinedField. */
  private loadClientDefinedFieldPipe(nv0: any): any {
    const cfRaw = nv0?.value;
    if (!cfRaw) return undefined;

    const parts = cfRaw.split('|');
    const fieldNumber = parseInt(parts[0], 10) || 0;
    const operator = parts[1] || null;
    const valueText = parts[2] || null;
    const valueFrom = parts[3] ? parseFloat(parts[3]) : null;
    const valueTo = parts[4] ? parseFloat(parts[4]) : null;
    const days = parts[5] ? parseInt(parts[5], 10) : null;
    const notifyDays = parts[6] ? parseInt(parts[6], 10) : null;
    const bvLower = (parts[7] || '').trim().toLowerCase();
    const booleanValue =
      bvLower === 'true' || bvLower === 'y' || bvLower === 'yes'
        ? true
        : bvLower === 'false' || bvLower === 'n' || bvLower === 'no'
          ? false
          : null;
    const values = parts[8] ? parts[8].split('^') : [];
    const dataTypeRaw = parts.length > 9 ? parts[9] : '';
    const dataType =
      dataTypeRaw != null && String(dataTypeRaw).trim() !== ''
        ? String(dataTypeRaw).trim()
        : undefined;
    const fieldName =
      nv0?.name?.trim() && !/^Value\s*1$/i.test(nv0.name.trim())
        ? nv0.name.trim()
        : undefined;

    return {
      fieldNumber,
      fieldName,
      dataType,
      operator,
      valueText,
      valueFrom,
      valueTo,
      days,
      notifyDays,
      booleanValue,
      values,
    };
  }

  /** Parses the new named-entry format for ClientDefinedField. */
  private loadClientDefinedFieldNamed(nvArr: any[], alert: any): any {
    const dict: Record<string, string> = {};
    for (const entry of nvArr) {
      if (entry?.name) dict[entry.name] = entry.value ?? '';
    }

    const fieldNumber = parseInt(dict['FieldNum'], 10) || 0;
    const dataType = dict['DataType'] || undefined;
    const isLookup = dict['IsLookup'] === 'true';
    const mcfValue = dict['MCFValue'] || '';
    const notifyDays = dict['NotifyDays']
      ? parseInt(dict['NotifyDays'], 10)
      : null;
    const fieldName = dict['FieldName'] || undefined;

    const wcfOp = alert.condition?.operatorType;
    const operator = this.mapWcfOperatorToCustomField(wcfOp);

    const dtLower = (dataType || '').toLowerCase();
    let valueText: string | null = null;
    let valueFrom: number | null = null;
    let valueTo: number | null = null;
    let days: number | null = null;
    let booleanValue: boolean | null = null;
    let values: string[] = [];

    if (isLookup && mcfValue) {
      values = mcfValue.split(',');
    } else if (dtLower === 'logical' || dtLower === 'boolean') {
      booleanValue =
        mcfValue === 'true' || mcfValue === 'Y' || mcfValue === 'YES'
          ? true
          : mcfValue === 'false' || mcfValue === 'N' || mcfValue === 'NO'
            ? false
            : null;
    } else if (dtLower === 'date') {
      days = mcfValue ? parseInt(mcfValue, 10) || null : null;
    } else if (dtLower === 'integer' || dtLower === 'decimal') {
      if (wcfOp === 'Between' && mcfValue.includes(',')) {
        const parts = mcfValue.split(',');
        valueFrom = parseFloat(parts[0]) || null;
        valueTo = parseFloat(parts[1]) || null;
      } else {
        valueFrom = mcfValue ? parseFloat(mcfValue) || null : null;
      }
    } else {
      valueText = mcfValue || null;
    }

    return {
      fieldNumber,
      fieldName,
      dataType,
      operator,
      valueText,
      valueFrom,
      valueTo,
      days,
      notifyDays,
      booleanValue,
      values,
    };
  }

  private mapWcfOperatorToCustomField(
    wcfOp: string | null | undefined,
  ): string | null {
    if (!wcfOp || wcfOp === 'None') return null;
    const map: Record<string, string> = {
      InList: 'inList',
      NotInList: 'notInList',
      EqualTo: 'equalTo',
      NotEqualTo: 'notEqualTo',
      GreaterThan: 'greaterThan',
      GreaterThanEqualTo: 'greaterThanOrEqualTo',
      LessThan: 'lessThan',
      LessThanEqualTo: 'lessThanOrEqualTo',
      Between: 'between',
      Like: 'like',
      Boolean: 'equals',
      IsNull: 'isNull',
      IsNotNull: 'isNotNull',
    };
    return map[wcfOp] || null;
  }

  async loadConditionCodes(
    contractNumber: number,
    codeType: 'Cause' | 'Nature' | 'BodyPart',
    lobs: string[],
  ): Promise<ListItem[]> {
    return this.api.newAlert.getConditionCodes({
      contractNumber,
      codeType,
      lobs,
    });
  }

  async loadPayCodes(lobs: string[]): Promise<void> {
    if (!lobs || lobs.length === 0) {
      this.payCodeTypes = [];
      return;
    }

    const payCodes = await this.api.newAlert.getPayCodes({ lobs });
    this.payCodeTypes = this.buildPayCodeGroups(payCodes);
  }

  async loadMiscFields(
    contractNumber: number,
    lobs: string[],
    search?: string,
  ): Promise<AlertMiscField[]> {
    return this.api.newAlert.getMiscFields({
      contractNumber,
      lobs,
      search,
    });
  }

  async loadMiscFieldValues(
    contractNumber: number,
    fieldNumber: number,
  ): Promise<AlertMiscFieldValue[]> {
    return this.api.newAlert.getMiscFieldValues({
      contractNumber,
      fieldNumber,
    });
  }

  async searchIndividuals(request: {
    contractNumber: number;
    firstName?: string;
    lastName?: string;
    employeeId?: string;
    ssn?: string;
    useSsnLast4: boolean;
    hireStartDate?: string;
    hireEndDate?: string;
    source?: string;
  }): Promise<EmployeeValue[]> {
    return this.api.newAlert.searchIndividuals(request);
  }

  createNewAlert(): void {
    this.storage.remove('alerts:current-alert');

    const newAlert: Alert = {
      alertId: 0,
      userId: '',
      description: '',
      enabled: true,
      sendEmail: false,
      sendText: false,
      aggregateEmail: false,
      includeAttachment: false,
      maxTextsPerDay: 10,
      notificationFreq: 'Daily',
      locationIncludeType: 'Include',
      locationLookupType: 'Unknown',
      locationText: '',
      condition: null as any,
      linesOfBusiness: [],
      linesOfBusinessCard: [],
      emailAddresses: [],
      contracts: [],
      locations: [],
      statesOfLoss: [],
      statesOfJurisdiction: [],
      statesOfJurisdictionText: '',
      statesOfLossText: '',
      statesOfPayroll: [],
      statesOfPayrollText: '',
      statesOfPremium: [],
      statesOfPremiumText: '',
      smsPhones: [],
      additionalEmails: [],
    };
    this.currentAlert.set(newAlert);
    this.isEditMode.set(false);
  }

  clearAlert(): void {
    this.currentAlert.set(null);
    this.isEditMode.set(false);
    this.storage.remove('alerts:current-alert');
  }

  private add(
    items: ListItem[],
    description: string,
    value: any,
    disabled = false,
  ) {
    items.push({
      description,
      value,
      items: [],
      disabled,
    });
  }

  private addPickListItem(list: PickListGroup[], label: string, items: any[]) {
    list.push(new PickListGroup(label, items));
  }

  private buildPayCodeGroups(payCodes: AlertPayCode[]): PickListGroup[] {
    const grouped = new Map<number, any[]>();
    for (const code of payCodes) {
      const series = code.series ?? this.getSeriesFromPayCode(code.payCode);
      if (!series) {
        continue;
      }

      const items = grouped.get(series) ?? [];
      const desc = code.description ? ` ${code.description}` : '';
      let label = `${code.payCode}${desc}`;
      const lob = code.lineOfBusiness?.trim();
      if (lob) {
        const suffix = `(${lob})`.toUpperCase();
        if (!label.trimEnd().toUpperCase().endsWith(suffix)) {
          label = `${label} (${lob})`;
        }
      }
      items.push({
        label,
        value: code.payCode,
        lineOfBusiness: code.lineOfBusiness,
      });
      grouped.set(series, items);
    }

    const groups: PickListGroup[] = [];
    const sortedSeries = Array.from(grouped.keys()).sort((a, b) => a - b);
    for (const series of sortedSeries) {
      const label = this.getSeriesLabel(series);
      const items = grouped.get(series) ?? [];
      items.sort((a, b) => a.label.localeCompare(b.label));
      groups.push(new PickListGroup(label, items));
    }

    return groups;
  }

  private getSeriesFromPayCode(payCode?: string): number | null {
    const code = payCode ? parseInt(payCode, 10) : NaN;
    if (Number.isNaN(code)) {
      return null;
    }

    return Math.floor(code / 100) * 100;
  }

  private getSeriesLabel(series: number): string {
    const labels: Record<number, string> = {
      100: '100 Series (Indemnity/Loss)',
      200: '200 Series (Loss)',
      300: '300 Series (Medical)',
      400: '400 Series (Loss)',
      500: '500 Series (Loss)',
      600: '600 Series (Expense)',
      700: '700 Series (Recovery)',
    };

    return labels[series] ?? `${series} Series`;
  }

  getNotificationFrequencyFromString(freq: string): NotificationFrequency {
    switch (freq) {
      case 'daily':
        return 'Daily';
      case 'nrt':
        return 'NearRealTime';
      default:
        return 'Unknown';
    }
  }

  getNotificationFrequencyString(freq: NotificationFrequency): string {
    switch (freq) {
      case 'Daily':
        return 'daily';
      case 'NearRealTime':
        return 'nrt';
      default:
        return '';
    }
  }

  private getConditionValue(
    condition: Condition,
    index: number,
  ): string | null {
    if (!condition?.newValues?.length) {
      return null;
    }

    if (index < condition.newValues.length) {
      return condition.newValues[index]?.value ?? null;
    }

    return null;
  }

  /**
   * Rebuilds Event within policy limit condition text from a stored alert condition.
   * Supports pipe format in value1 (percent|financial|notifyDays) or separate newValues entries.
   */
  getPolicyLimitConditionTextFromCondition(
    condition: Condition | null | undefined,
  ): string | null {
    if (String(condition?.conditionType) !== 'PolicyLimit') {
      return null;
    }

    const { percent, financial, notifyDays } =
      this.parsePolicyLimitCondition(condition);
    let text: string;

    if (financial && percent) {
      text = `The ${financial} amount for the claim/event is within ${percent} of the policy limit.`;
    } else if (percent) {
      text = `The claim/event amount is within ${percent} of the policy limit.`;
    } else {
      text = 'Policy limit condition.';
    }

    if (notifyDays != null && notifyDays > 0) {
      const label = notifyDays === 1 ? '1 day' : `${notifyDays} days`;
      text += `\nSend notifications for ${label}`;
    }

    return text;
  }

  /**
   * Rebuilds conditionText on PolicyLimit alert messages from alert definitions.
   */
  async enrichPolicyLimitMessageConditions(
    messages: AlertMessage[],
  ): Promise<void> {
    if (!messages.length) {
      return;
    }

    const policyLimitConditions = new Map<number, Condition>(
      (await this.alertsDataService.getAlerts())
        .filter(
          (alert) =>
            alert.alertId != null &&
            String(alert.condition?.conditionType) === 'PolicyLimit',
        )
        .map((alert) => [alert.alertId!, alert.condition!]),
    );

    if (!policyLimitConditions.size) {
      return;
    }

    for (const message of messages) {
      if (message.alertId == null) {
        continue;
      }

      const text = this.getPolicyLimitConditionTextFromCondition(
        policyLimitConditions.get(message.alertId),
      );
      if (text) {
        message.conditionText = text;
      }
    }
  }

  private parsePolicyLimitCondition(condition: Condition): {
    percent?: string;
    financial?: string;
    notifyDays?: number;
  } {
    const value1 = this.getConditionValue(condition, 0)?.trim() ?? '';
    const value2 = this.getConditionValue(condition, 1)?.trim();
    const newValues = condition.newValues ?? [];

    let percentRaw: string | undefined;
    let financialRaw: string | undefined;
    let notifyRaw: string | undefined;

    if (value1.includes('|')) {
      const [percentPart, financialPart, notifyPart] = value1.split('|');
      percentRaw = percentPart?.trim() || undefined;
      financialRaw =
        financialPart?.trim() ||
        newValues[2]?.value?.trim() ||
        undefined;
      notifyRaw =
        notifyPart?.trim() ||
        value2 ||
        newValues[1]?.value?.trim() ||
        undefined;
    } else if (value1) {
      percentRaw = value1;
      financialRaw = newValues[2]?.value?.trim() || undefined;
      notifyRaw = value2 || newValues[1]?.value?.trim() || undefined;
    }

    if (financialRaw?.toLowerCase() === 'none') {
      financialRaw = undefined;
    }

    const financialKey = financialRaw?.toLowerCase();
    const financial =
      financialKey != null && financialKey !== ''
        ? ({ incurred: 'Incurred', paid: 'Paid' }[financialKey] ??
          financialRaw!.trim())
        : undefined;

    const percent = percentRaw
      ? `${percentRaw.replace(/%$/, '')}%`
      : undefined;
    const notifyDays = notifyRaw ? parseInt(notifyRaw, 10) : NaN;

    return {
      percent,
      financial,
      notifyDays: Number.isNaN(notifyDays) ? undefined : notifyDays,
    };
  }

  /**
   * Builds the condition display text for Custom field (ClientDefinedField) from stored value1 and field name.
   * Used when displaying alerts in Manage Alerts so the Condition section shows the formatted expression
   * instead of the card description (e.g. for alerts saved before condition.Text was populated, or when API returns empty).
   * value1 format: fieldNumber|operator|valueText|valueFrom|valueTo|days|notifyDays|booleanValue|values(^)|dataType
   */
  getCustomFieldConditionDisplayText(
    fieldName: string,
    value1: string,
    listValueDescriptions?: Map<string, string>,
  ): string {
    if (!value1?.trim()) {
      return fieldName || 'Custom field';
    }

    const parts = value1.split('|');
    const op = parts.length > 1 ? parts[1] : null;
    const valueText = parts.length > 2 ? parts[2] : null;
    const valueFrom = parts.length > 3 ? parts[3] : null;
    const valueTo = parts.length > 4 ? parts[4] : null;
    const days = parts.length > 5 ? parts[5] : null;
    const notifyDays = parts.length > 6 ? parts[6] : null;
    const boolVal = parts.length > 7 ? parts[7] : null;
    const valuesList = parts.length > 8 ? parts[8] : null;
    const dataType =
      parts.length > 9 && parts[9] != null && String(parts[9]).trim() !== ''
        ? String(parts[9]).trim()
        : '';

    const name = fieldName?.trim() || 'Custom field';
    const ensurePeriod = (s: string) =>
      s && !s.endsWith('.') ? `${s}.` : s || '';

    const opLabel: Record<string, string> = {
      equalTo: 'equal to',
      notEqualTo: 'not equal to',
      isNull: 'null',
      isNotNull: 'not null',
      like: 'like',
      inList: 'In list',
      notInList: 'Not in list',
      greaterThanOrEqualTo: 'greater than or equal to',
      lessThanOrEqualTo: 'less than or equal to',
      between: 'between',
      greaterThan: 'greater than',
      lessThan: 'less than',
    };

    const opKey = op ? String(op) : '';
    const opNorm = opKey.toLowerCase();
    const resolveOpLabel = (): string => {
      if (!opKey) {
        return '';
      }
      if (opLabel[opKey] !== undefined) {
        return opLabel[opKey];
      }
      const canon = Object.keys(opLabel).find(
        (k) => k.toLowerCase() === opNorm,
      );
      return canon !== undefined ? opLabel[canon] : opKey;
    };

    const boolLower =
      boolVal != null && boolVal !== ''
        ? String(boolVal).trim().toLowerCase()
        : '';
    const isLogicalType = dataType.toLowerCase() === 'logical';
    const isLogicalBool = boolLower === 'true' || boolLower === 'false';

    // DATE (days + notify in dedicated slots; must run before logical if both ever conflict)
    const daysT = days != null ? String(days).trim() : '';
    const notifyT = notifyDays != null ? String(notifyDays).trim() : '';
    if (daysT !== '' && notifyT !== '') {
      const daysN = parseInt(daysT, 10);
      const daysLabel =
        daysN === 1 ? '1 day' : !Number.isNaN(daysN) ? `${daysN} days` : '';
      const n = parseInt(notifyT, 10);
      const notifyLabel =
        n === 1 ? '1 day' : !Number.isNaN(n) ? `${n} days` : '';
      let text = `${name} days is greater than or equal to ${daysLabel || `${daysT} days`}.`;
      if (notifyLabel) {
        text += `\nSend notifications for ${notifyLabel}.`;
      }
      return ensurePeriod(text);
    }

    // LOGICAL (Yes/No only; bool in slot 7 wins over invalid text operators)
    if (isLogicalType || isLogicalBool) {
      const yesNo = boolLower === 'true' ? 'Yes' : 'No';
      return ensurePeriod(`${name} is ${yesNo}`.trim());
    }

    // INTEGER/DECIMAL
    const numericOps = [
      'greaterThanOrEqualTo',
      'lessThanOrEqualTo',
      'equalTo',
      'notEqualTo',
      'between',
      'greaterThan',
      'lessThan',
    ];
    const isNumericOp =
      op &&
      numericOps.some((o) => o.toLowerCase() === (op as string).toLowerCase());
    if (isNumericOp) {
      const label = resolveOpLabel();
      if (opNorm === 'between') {
        let fromT = (valueFrom ?? '').toString().trim();
        let toT = (valueTo ?? '').toString().trim();
        const textT = (valueText ?? '').toString().trim();
        // Legacy pipe rows sometimes put bounds in valueText|valueFrom with valueTo empty
        if (toT === '' && textT !== '' && fromT !== '') {
          toT = fromT;
          fromT = textT;
        }
        if (fromT !== '' && toT !== '') {
          return ensurePeriod(`${name} is ${label} ${fromT} and ${toT}`.trim());
        }
      }
      const fromT = (valueFrom ?? '').toString().trim();
      const textT = (valueText ?? '').toString().trim();
      const numVal = fromT !== '' ? fromT : textT;
      const suffix = numVal ? ` ${numVal}` : '';
      return ensurePeriod(`${name} is ${label}${suffix}`.trim());
    }

    // CHARACTER (list values: show API val_desc / description when lookup provided)
    const charOpLabel = op ? resolveOpLabel() : '';
    if (opNorm === 'isnull' || opNorm === 'isnotnull') {
      return ensurePeriod(`${name} is ${charOpLabel}`.trim());
    }
    let charVal;
    if (valuesList != null && String(valuesList).trim() !== '') {
      const codes = String(valuesList)
        .split('^')
        .map((c) => c.trim())
        .filter(Boolean);
      charVal = codes
        .map((code) => listValueDescriptions?.get(code) ?? code)
        .join(', ');
    } else {
      charVal = (valueText ?? '').toString().trim();
    }
    const charSuffix = charVal ? ` ${charVal}` : '';
    return ensurePeriod(`${name} is ${charOpLabel}${charSuffix}`.trim());
  }

  /*
  private mapOperatorTypeToAngular(
    opType: string | null | undefined,
  ): string | null {
    if (!opType) {
      return null;
    }
    switch (opType) {
      case 'InList':
        return 'inList';
      case 'NotInList':
        return 'notInList';
      case 'EqualTo':
        return 'equalTo';
      case 'NotEqualTo':
        return 'notEqualTo';
      case 'GreaterThanEqualTo':
        return 'greaterThanOrEqualTo';
      case 'LessThanEqualTo':
        return 'lessThanOrEqualTo';
      case 'GreaterThan':
        return 'greaterThan';
      case 'LessThan':
        return 'lessThan';
      case 'Between':
        return 'between';
      case 'IsNull':
        return 'isNull';
      case 'IsNotNull':
        return 'isNotNull';
      case 'Like':
        return 'like';
      case 'Boolean':
        return 'equals';
      default:
        return opType;
    }
  }*/
}
